import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useQuote } from '../context/QuoteContext';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openQuote } = useQuote();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Science', path: '/science' },
    { name: 'Products', path: '/products' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/90 backdrop-blur-2xl shadow-[0_1px_0_rgba(0,0,0,0.04),0_4px_20px_rgba(0,0,0,0.03)]' : 'bg-white/60 backdrop-blur-xl'}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[72px]">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="relative w-14 h-14">
                <img
                  src="/Aerohix-logo.jpeg"
                  alt="Arohix Logo"
                  className="w-full h-full rounded-[10px] object-contain group-hover:scale-125 transition-all duration-300"
                />
              </div>
            </Link>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path}
                  className={`relative px-4 py-2 text-[13px] font-medium tracking-wide rounded-lg transition-all duration-300 group
                    ${isActive(link.path) ? 'text-brand-600' : 'text-silver-500 hover:text-silver-900'}`}
                >
                  {link.name}
                  <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] rounded-full bg-gradient-to-r from-brand-500 to-brand-400 transition-all duration-300
                    ${isActive(link.path) ? 'w-5 opacity-100' : 'w-0 opacity-0 group-hover:w-4 group-hover:opacity-50'}`} />
                </Link>
              ))}
            </div>

            {/* Get Quote CTA */}
            <div className="hidden lg:flex items-center">
              <button onClick={() => openQuote()}
                className="shimmer-btn group relative px-6 py-2.5 text-[13px] font-semibold rounded-full overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-brand-500/20 hover:scale-[1.02] active:scale-[0.98]">
                <div className="absolute inset-0 bg-gradient-to-r from-brand-600 to-brand-500 transition-all duration-300" />
                <span className="relative text-white flex items-center gap-2">
                  Get Quote
                  <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                </span>
              </button>
            </div>

            {/* Mobile Toggle */}
            <button className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl hover:bg-silver-100 transition-colors" onClick={() => setIsOpen(!isOpen)}>
              {isOpen
                ? <X className="w-5 h-5 text-silver-800" />
                : <Menu className="w-5 h-5 text-silver-800" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="bg-white/95 backdrop-blur-2xl border-t border-silver-100/80 px-5 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path}
                className={`block px-4 py-3 rounded-xl text-[13px] font-medium transition-all duration-200 ${isActive(link.path) ? 'bg-brand-50 text-brand-700' : 'text-silver-600 hover:bg-silver-50'}`}>
                {link.name}
              </Link>
            ))}
            <button onClick={() => { openQuote(); setIsOpen(false); }}
              className="block w-full mt-3 px-4 py-3 bg-gradient-to-r from-brand-600 to-brand-500 text-white rounded-xl text-[13px] font-semibold text-center">
              Get Quote
            </button>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
